import { Check, ExternalLink } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { COOKIE_UPDATED, COOKIE_INTRO, COOKIE_SECTIONS } from "../data/cookies";

const CookieTable = ({ table }) => (
  <div className="mt-4 overflow-x-auto rounded-xl border border-[rgba(67,157,255,0.35)]">
    <table className="w-full text-left text-sm border-collapse">
      <thead>
        <tr className="bg-[rgba(8,29,59,0.9)]">
          {table.headers.map((h) => (
            <th key={h} className="px-4 py-3 font-bold text-[#F7FAFF] border-b border-[rgba(67,157,255,0.35)] whitespace-nowrap">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {table.rows.map((row, i) => (
          <tr key={i} className="odd:bg-[rgba(6,21,45,0.5)]">
            {row.map((cell, j) => (
              <td key={j} className="px-4 py-3 text-[#AEBFD8] border-b border-[rgba(67,157,255,0.15)] align-top">{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ExtLink = ({ url, label }) => (
  <a href={url} target="_blank" rel="noopener noreferrer"
    className="inline-flex items-center gap-1.5 text-[#39D5FF] hover:underline font-medium">
    {label} <ExternalLink size={13} />
  </a>
);

export default function CookiePolicyPage() {
  return (
    <div className="pt-28 md:pt-32 px-5 md:px-10 pb-10 md:pb-16">
      <div className="max-w-[980px] mx-auto">
        <Reveal>
          <div className="glass-panel px-6 py-12 md:px-14 md:py-16">
            <header className="mb-10">
              <p className="text-xs font-bold tracking-[0.22em] text-[#39D5FF] mb-4">LEGAL</p>
              <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-[#F7FAFF]">
                Cookie <span className="text-gradient">Policy.</span>
              </h1>
              <p className="mt-4 text-sm text-[#7F92AE]">Last updated: {COOKIE_UPDATED}</p>
            </header>

            <div className="space-y-4 mb-12">
              {COOKIE_INTRO.map((p, i) => (
                <p key={i} className="text-[15px] md:text-base text-[#AEBFD8] leading-relaxed">{p}</p>
              ))}
            </div>

            <div className="space-y-12">
              {COOKIE_SECTIONS.map((s) => (
                <section key={s.title} data-testid="cookie-section">
                  <h2 className="text-xl md:text-2xl font-bold text-[#F7FAFF] tracking-tight mb-3 pl-4 border-l-2 border-[rgba(65,176,255,0.65)]">
                    {s.title}
                  </h2>
                  {s.paras?.map((p, i) => (
                    <p key={i} className="text-[15px] md:text-base text-[#AEBFD8] leading-relaxed mt-3">{p}</p>
                  ))}

                  {s.subsections?.map((sub) => (
                    <div key={sub.heading} className="mt-6">
                      <h3 className="text-base md:text-lg font-semibold text-[#39D5FF] mb-2">{sub.heading}</h3>
                      {sub.paras?.map((p, i) => (
                        <p key={i} className="text-[15px] md:text-base text-[#AEBFD8] leading-relaxed mt-2">{p}</p>
                      ))}
                      {sub.table && <CookieTable table={sub.table} />}
                      {sub.after?.map((p, i) => (
                        <p key={i} className="text-[15px] md:text-base text-[#AEBFD8] leading-relaxed mt-3">{p}</p>
                      ))}
                      {sub.afterLink && <p className="mt-2 text-[15px]">For more information, see the <ExtLink {...sub.afterLink} />.</p>}
                      {sub.links && (
                        <ul className="mt-3 space-y-2.5">
                          {sub.links.map((l) => (
                            <li key={l.label} className="flex items-start gap-3 text-[15px] text-[#AEBFD8]">
                              <Check size={16} className="text-[#45E0B5] mt-1 shrink-0" />
                              <ExtLink {...l} />
                            </li>
                          ))}
                        </ul>
                      )}
                      {sub.link && <p className="mt-3 text-[15px]"><ExtLink {...sub.link} /></p>}
                    </div>
                  ))}

                  {s.contact && (
                    <div className="glass-card mt-5 p-5 text-[15px] text-[#AEBFD8] leading-relaxed">
                      <p className="font-bold text-[#F7FAFF]">{s.contact.name}</p>
                      <p>{s.contact.address}</p>
                      <p className="mt-1">Email: <a href={`mailto:${s.contact.email}`} className="text-[#39D5FF] hover:underline">{s.contact.email}</a></p>
                      <p>Website: <a href={s.contact.website} target="_blank" rel="noopener noreferrer" className="text-[#39D5FF] hover:underline">{s.contact.website}</a></p>
                    </div>
                  )}
                </section>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
